'use client';

import { useState, useEffect, useRef } from 'react';
import { ChevronRight } from 'lucide-react';

interface TocItem {
  id: string;
  text: string;
  level: number;
  element: HTMLElement;
}

interface TableOfContentsProps {
  className?: string;
}

export default function TableOfContents({ className = '' }: TableOfContentsProps) {
  const [tocItems, setTocItems] = useState<TocItem[]>([]);
  const [activeId, setActiveId] = useState<string>('');
  const observerRef = useRef<IntersectionObserver | null>(null);

  // Extract headings from the actual DOM elements
  const extractHeadingsFromDOM = (): TocItem[] => {
    const headings: TocItem[] = [];

    // Add article overview as the first item
    const articleHeader = document.querySelector('article header');
    if (articleHeader) {
      headings.push({
        id: 'article-overview',
        text: 'Article Overview',
        level: 0, // Special level for overview
        element: articleHeader as HTMLElement
      });
    }

    // Look for headings in the article content area
    const articleContent = document.querySelector('.prose');
    if (!articleContent) return headings;

    const headingElements = articleContent.querySelectorAll('h1, h2, h3, h4, h5, h6');

    headingElements.forEach((element) => {
      const id = element.id;
      const text = element.textContent?.trim() || '';
      const level = parseInt(element.tagName.charAt(1));

      if (id && text) {
        headings.push({
          id,
          text,
          level,
          element: element as HTMLElement
        });
      }
    });

    return headings;
  };

  // Set up intersection observer for active section tracking
  useEffect(() => {
    if (tocItems.length === 0) return;

    // Set up intersection observer
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveId(entry.target.id);
          }
        });
      },
      {
        rootMargin: '-20% 0px -70% 0px', // Trigger when heading is in the top 30% of viewport
        threshold: 0
      }
    );

    // Observe all heading elements
    tocItems.forEach(item => {
      if (item.element) {
        observerRef.current?.observe(item.element);
      }
    });

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [tocItems]);

  // Extract headings from DOM after component mounts and content is rendered
  useEffect(() => {
    // Use a timeout to ensure the content is fully rendered
    const timer = setTimeout(() => {
      const headings = extractHeadingsFromDOM();
      setTocItems(headings);

      // Set article overview as active by default if it exists, otherwise first heading
      if (headings.length > 0) {
        const overviewItem = headings.find(h => h.id === 'article-overview');
        setActiveId(overviewItem ? overviewItem.id : headings[0].id);
      }
    }, 100);

    return () => clearTimeout(timer);
  }, []);

  // Smooth scroll to section
  const scrollToSection = (id: string) => {
    let element: HTMLElement | null = null;

    if (id === 'article-overview') {
      // For article overview, scroll to the article header
      element = document.querySelector('article header') as HTMLElement;
    } else {
      element = document.getElementById(id);
    }

    if (element) {
      const offset = 100; // Account for fixed header
      const elementPosition = element.offsetTop - offset;

      window.scrollTo({
        top: elementPosition,
        behavior: 'smooth'
      });
    }
  };

  // Don't render if no headings found
  if (tocItems.length === 0) {
    return null;
  }

  return (
    <div className={`bg-gray-50 dark:bg-brand-dark-lighter rounded-lg p-6 ${className}`}>
      <h3 className="font-bold text-lg text-gray-900 dark:text-white mb-4">
        In This Article
      </h3>
      <div className="text-sm text-gray-600 dark:text-gray-300">
        <p className="mb-3">Jump to sections:</p>
        <nav className="space-y-1">
          {tocItems.map((item, index) => (
            <button
              key={item.id}
              onClick={() => scrollToSection(item.id)}
              className={`
                w-full text-left px-3 py-2 rounded-md transition-all duration-200
                flex items-center group
                ${activeId === item.id
                  ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 font-medium'
                  : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-white'
                }
                ${item.level === 0 ? 'border-b border-gray-200 dark:border-gray-600 mb-2 pb-2' : ''}
              `}
              style={{
                paddingLeft: item.level === 0 ? '12px' : `${(item.level - 1) * 12 + 12}px`
              }}
            >
              <ChevronRight
                className={`
                  w-3 h-3 mr-2 transition-transform duration-200
                  ${activeId === item.id ? 'rotate-90' : 'rotate-0'}
                `}
              />
              <span className={`truncate ${item.level === 0 ? 'font-medium' : ''}`}>
                {item.text}
              </span>
            </button>
          ))}
        </nav>
      </div>
    </div>
  );
}
